<template>
  <thead role="rowgroup">
    <tr role="row">
      <!-- Selection checkbox column -->
      <th 
        v-if="selectable"
        class="themed-table-header-cell themed-table-cell-checkbox"
        role="columnheader"
        :style="{ width: '40px' }"
      >
        <input
          type="checkbox"
          :checked="allSelected"
          @change="$emit('select-all', $event.target.checked)"
          @keydown.space.prevent="handleSelectAllKeydown"
          :aria-label="allSelected ? 'Deselect all rows' : 'Select all rows'"
          :disabled="disabled"
          tabindex="0"
        />
      </th>
      
      <!-- Column headers -->
      <th
        v-for="(column, index) in columns"
        :key="column.field || index"
        class="themed-table-header-cell"
        :class="[
          column.class,
          {
            'themed-table-sortable': column.sortable && !disabled,
            'themed-table-sorted': sortColumn === column.field,
            [`themed-table-align-${column.align}`]: column.align
          }
        ]"
        :style="getColumnStyle(column)"
        role="columnheader"
        :aria-sort="getSortAriaAttribute(column)"
        :tabindex="column.sortable && !disabled ? '0' : null"
        :aria-label="getColumnAriaLabel(column)"
        @click="column.sortable && !disabled ? $emit('sort', column.field) : null"
        @keydown.enter="column.sortable && !disabled ? $emit('sort', column.field) : null"
        @keydown.space.prevent="column.sortable && !disabled ? $emit('sort', column.field) : null"
      >
        <div class="themed-table-header-content">
          <span class="themed-table-header-label">{{ column.label }}</span>
          <span 
            v-if="column.sortable" 
            class="themed-table-sort-indicator"
            :class="{
              'themed-table-sort-asc': sortColumn === column.field && sortDirection === 'asc',
              'themed-table-sort-desc': sortColumn === column.field && sortDirection === 'desc'
            }"
            aria-hidden="true"
          >
            <span v-if="sortColumn !== column.field">⇅</span>
            <span v-else-if="sortDirection === 'asc'">↑</span>
            <span v-else>↓</span>
          </span>
        </div>
        
        <!-- Column resize handle -->
        <div
          v-if="column.resizable !== false"
          class="themed-table-resize-handle"
          @mousedown.stop="handleResizeStart($event, column)"
          @click.stop
          :aria-label="`Resize ${column.label} column`"
          role="separator"
        >
          <theme-icon icon="resize-column" type="icons" classes="themed-table-resize-icon" />
        </div>
      </th>
    </tr>
  </thead>
</template>

<script>
import ThemeIcon from '../ThemeIcon.vue';

export default {
  name: 'TableHeader',
  components: {
    ThemeIcon
  },
  props: {
    columns: {
      type: Array,
      required: true
    },
    sortColumn: {
      type: String,
      default: null
    },
    sortDirection: {
      type: String,
      default: 'asc',
      validator: (v) => ['asc', 'desc'].includes(v)
    },
    selectable: {
      type: Boolean,
      default: false
    },
    allSelected: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    columnWidths: {
      type: Object,
      default: () => ({})
    }
  },
  methods: {
    getColumnStyle(column) {
      const styles = {};
      
      // Apply dynamic width from columnWidths (takes priority)
      if (this.columnWidths[column.field]) {
        styles.width = `${this.columnWidths[column.field]}px`;
        styles.minWidth = `${this.columnWidths[column.field]}px`;
        styles.maxWidth = `${this.columnWidths[column.field]}px`;
      }
      // Apply column width if specified in column config
      else if (column.width) {
        styles.width = column.width;
      }
      
      return styles;
    },
    
    getSortAriaAttribute(column) {
      if (!column.sortable) {
        return null;
      }
      
      if (this.sortColumn === column.field) {
        return this.sortDirection === 'asc' ? 'ascending' : 'descending';
      }
      
      return 'none';
    },
    
    getColumnAriaLabel(column) {
      if (!column.sortable || this.disabled) {
        return column.label;
      }
      
      if (this.sortColumn === column.field) {
        const direction = this.sortDirection === 'asc' ? 'ascending' : 'descending';
        return `${column.label}, sorted ${direction}. Click to change sort direction.`;
      }
      
      return `${column.label}. Click to sort.`;
    },
    
    handleSelectAllKeydown(event) {
      // Toggle checkbox on space key
      const checkbox = event.target;
      checkbox.checked = !checkbox.checked;
      this.$emit('select-all', checkbox.checked);
    },
    
    handleResizeStart(event, column) {
      // Get the th element
      const th = event.target.closest('th');
      const startWidth = th.offsetWidth;
      const startX = event.clientX;
      
      // Emit resize-start event
      this.$emit('resize-start', {
        column,
        startX,
        startWidth
      });
    }
  }
}
</script>
